import {
  InlineError
} from "./chunk-XU42F452.mjs";

// src/Blog/Blog.tsx
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import { Card, CardMedia, CardContent, Typography, Box } from "@mui/material/index.js";
import { useTranslation } from "react-i18next";

// src/Blog/imageUrl.ts
var resolveBlogImageUrl = (baseURL, imageData, formatPriority) => {
  if (!imageData) return null;
  for (const key of formatPriority) {
    const url = imageData.formats?.[key]?.url;
    if (url) return `${baseURL ?? ""}${url}`;
  }
  return imageData.url ? `${baseURL ?? ""}${imageData.url}` : null;
};

// src/Blog/Blog.tsx
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var Blog = ({ cmsClient, pageNameFilter }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState(null);
  const baseURL = cmsClient.defaults.baseURL;
  const filters = Array.isArray(pageNameFilter) ? pageNameFilter : [pageNameFilter];
  const loadPosts = () => {
    setError(null);
    cmsClient.get("/api/posts?populate=*&sort=order:desc").then((response) => {
      setPosts(response.data?.data || []);
    }).catch((err) => {
      setError(err);
      setPosts([]);
    });
  };
  useEffect(() => {
    loadPosts();
  }, []);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    posts.length > 0 && /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(
      Typography,
      {
        component: "h1",
        sx: { fontWeight: "bold", fontSize: "36px", mb: 2 },
        children: t("blog.title")
      }
    ) }),
    /* @__PURE__ */ jsx(
      Box,
      {
        sx: {
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: "32px",
          alignItems: "stretch",
          justifyContent: "flex-start"
        },
        children: posts.length === 0 && error ? /* @__PURE__ */ jsx(Box, { sx: { width: "100%" }, children: /* @__PURE__ */ jsx(InlineError, { onRetry: loadPosts }) }) : posts.length === 0 ? /* @__PURE__ */ jsx(
          Box,
          {
            sx: {
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
              textAlign: "center"
            },
            children: /* @__PURE__ */ jsx(
              Typography,
              {
                variant: "h6",
                color: "text.secondary",
                sx: { fontSize: "18px" },
                children: t("blog.noPosts")
              }
            )
          }
        ) : posts.filter((post) => post.thumbnail).filter((post) => filters.includes(post.PageName ?? "")).map((post) => {
          const { id, title, header, thumbnail, documentId } = post;
          const imageUrl = resolveBlogImageUrl(baseURL, thumbnail, [
            "small",
            "thumbnail"
          ]);
          return /* @__PURE__ */ jsxs(
            Card,
            {
              sx: {
                maxWidth: 600,
                width: {
                  xs: "100%",
                  md: "31%"
                },
                minWidth: {
                  xs: "auto"
                },
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
                transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 25px rgba(0,0,0,0.15)"
                }
              },
              onClick: () => navigate(`/blog/${documentId}`),
              children: [
                /* @__PURE__ */ jsx(
                  CardMedia,
                  {
                    component: "img",
                    height: "300",
                    image: imageUrl ?? void 0,
                    alt: thumbnail?.alternativeText
                  }
                ),
                /* @__PURE__ */ jsxs(
                  CardContent,
                  {
                    sx: {
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column"
                    },
                    children: [
                      /* @__PURE__ */ jsx(
                        Typography,
                        {
                          gutterBottom: true,
                          variant: "h4",
                          component: "h1",
                          style: {
                            fontSize: "24px",
                            fontWeight: "bold",
                            lineHeight: "1.4"
                          },
                          children: title
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        Typography,
                        {
                          variant: "body1",
                          color: "text.secondary",
                          component: "div",
                          sx: {
                            flexGrow: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical"
                          },
                          children: /* @__PURE__ */ jsx(ReactMarkdown, { children: header })
                        }
                      )
                    ]
                  }
                )
              ]
            },
            id
          );
        })
      }
    )
  ] });
};

// src/Blog/BlogPost.tsx
import { useEffect as useEffect2, useState as useState2 } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown2 from "react-markdown";
import { Typography as Typography2, Box as Box2, Grid } from "@mui/material/index.js";
import { useTranslation as useTranslation2 } from "react-i18next";
import { Fragment as Fragment2, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var BlogPost = ({ cmsClient }) => {
  const { id } = useParams();
  const { t, i18n } = useTranslation2();
  const [post, setPost] = useState2(null);
  const [loading, setLoading] = useState2(true);
  const [error, setError] = useState2(null);
  const baseURL = cmsClient.defaults.baseURL;
  const loadPost = () => {
    setLoading(true);
    setError(null);
    cmsClient.get(`/api/posts/${id}?populate=*`).then((response) => {
      setPost(response.data.data);
      setLoading(false);
    }).catch((err) => {
      setError(err);
      setLoading(false);
    });
  };
  useEffect2(() => {
    loadPost();
  }, [id]);
  if (loading) {
    return /* @__PURE__ */ jsx2(Typography2, { variant: "h4", children: "Loading..." });
  }
  if (error && !post) {
    return /* @__PURE__ */ jsx2(InlineError, { onRetry: loadPost });
  }
  if (!post) {
    return /* @__PURE__ */ jsx2(Typography2, { variant: "h4", children: "Post not found" });
  }
  const { title, header, carousel, content_1, content_2, publishedAt } = post;
  const publishedDate = publishedAt ? new Intl.DateTimeFormat(i18n.language, {
    year: "numeric",
    month: "long",
    day: "numeric"
  }).format(new Date(publishedAt)) : null;
  return /* @__PURE__ */ jsxs2(Fragment2, { children: [
    /* @__PURE__ */ jsx2(
      Typography2,
      {
        component: "h1",
        sx: { mt: 4, mb: 1, fontWeight: "bold", fontSize: "36px" },
        children: title
      }
    ),
    header && /* @__PURE__ */ jsx2(
      Typography2,
      {
        variant: "h5",
        component: "h2",
        sx: { mt: 1, mb: 2, fontWeight: "medium" },
        children: header
      }
    ),
    publishedDate && /* @__PURE__ */ jsx2(Typography2, { variant: "body2", color: "text.secondary", sx: { mb: 5 }, children: t("blog.publishedOn", { date: publishedDate }) }),
    content_1 && /* @__PURE__ */ jsx2(ReactMarkdown2, { children: content_1 }),
    carousel && carousel.length > 0 && /* @__PURE__ */ jsx2(Box2, { sx: { mb: 4, mt: 6 }, children: /* @__PURE__ */ jsx2(
      Grid,
      {
        container: true,
        spacing: 2,
        justifyContent: carousel.length <= 2 ? "center" : "space-between",
        sx: { paddingLeft: 0 },
        children: carousel.map((carouselImage, index) => /* @__PURE__ */ jsx2(
          Grid,
          {
            item: true,
            xs: 12,
            sm: 6,
            md: 4,
            sx: { paddingLeft: "0 !important" },
            children: /* @__PURE__ */ jsxs2(Box2, { sx: { textAlign: "center", paddingLeft: 0 }, children: [
              /* @__PURE__ */ jsx2(
                Box2,
                {
                  component: "img",
                  width: "325px",
                  height: "325px",
                  src: resolveBlogImageUrl(baseURL, carouselImage, [
                    "large",
                    "medium"
                  ]) ?? void 0,
                  alt: carouselImage.alternativeText || `Zdj\u0119cie ${index + 1}`,
                  sx: {
                    objectFit: "cover",
                    borderRadius: 2,
                    boxShadow: 2
                  }
                }
              ),
              carouselImage.caption && /* @__PURE__ */ jsx2(
                Typography2,
                {
                  variant: "body2",
                  color: "text.secondary",
                  sx: { mt: 1, fontStyle: "italic" },
                  children: carouselImage.caption
                }
              )
            ] })
          },
          index
        ))
      }
    ) }),
    content_2 && /* @__PURE__ */ jsx2(ReactMarkdown2, { children: content_2 })
  ] });
};
export {
  Blog,
  BlogPost
};
